/**
 * teacherContentPatch – how a host's model/image publish becomes a Firestore write.
 *
 * `teacher_content_state` used to be rebuilt from the host's last snapshot and
 * written back whole. Two things publish it on different schedules: choosing a
 * lesson image writes at once, while model tweaks (explode, section, isolate) are
 * throttled to ~200 ms. Whichever write landed second carried the OTHER field's
 * stale value from the snapshot and silently erased it — drag Explode, click an
 * image inside the throttle window, and the class never sees the explode.
 *
 * The fix is to write only what changed. Within one scene a publish becomes a
 * MERGE of dotted field paths (`teacher_content_state.<field>`), so two writers
 * touching different fields cannot clobber each other. Each patched field also
 * gets a stamp in `touched_at.<field>` with this publish's `sync_id`, so a
 * student can tell which fields a publish actually changed instead of
 * re-applying the whole model (and undoing their own local gestures) whenever
 * the teacher merely switches image.
 *
 * A NEW scene still REPLACES the object, exactly as before: a lesson change
 * must not inherit the previous model's explode, section or highlighted parts.
 * Replacing on the first write of every scene also means every later merge
 * lands on an object this scene created, never on the last lesson's leftovers.
 *
 * `replacedScene` covers the gap between writing a replace and reading it
 * back. The host's snapshot still shows the old scene for a moment; without it
 * the next publish would see "different scene" and replace again, wiping the
 * field the first replace just set.
 *
 * Values: `null` is kept, because it is meaningful ("image closed", "no cut").
 * `undefined` is dropped, because Firestore rejects it and it means "not part
 * of this patch". Nested objects such as `clip` or `model_transform` are
 * written whole at their path: they are one value to a reader, and merging
 * half of a clip plane with half of the old one would describe a plane nobody
 * chose.
 *
 * Pure: no Firebase import (the type below is erased at build), so every rule
 * here is tested in tests/teacherContentPatch.test.mts.
 */

import type { TeacherContentState } from '../../types/lms';

/**
 * The write to send. `data` is ready for `updateDoc`: in replace mode it holds
 * the whole `teacher_content_state` object; in merge mode it holds dotted paths.
 */
export type TeacherContentWrite = { mode: 'replace' | 'merge'; data: Record<string, unknown> };

export interface TeacherContentWriteContext {
  /** Stable identity of the scene the patch belongs to. */
  sceneKey: string;
  /** This publish's id; becomes `sync_id` and every `touched_at` stamp. */
  syncId: number;
  /** The state as the host last read it, or null before the first read. */
  previous: TeacherContentState | null;
  /** Scene this host already replaced the state for, even if not read back yet. */
  replacedScene: string | null;
}

const FIELD = 'teacher_content_state';

/**
 * Keys that describe the state itself rather than something the teacher chose.
 * They are never stamped in `touched_at` — a stamp means "the teacher changed
 * this", and none of these is a teacher choice. `scene_key`, `sync_id` and
 * `touched_at` are always set from the context, never taken from a patch, so a
 * patch cannot contradict the scene the write was decided for.
 * `licensed_content_id` is the one meta key a patch may set (written, unstamped).
 */
const META_KEYS: ReadonlySet<string> = new Set(['scene_key', 'licensed_content_id', 'sync_id', 'touched_at']);
const CONTEXT_OWNED_KEYS: ReadonlySet<string> = new Set(['scene_key', 'sync_id', 'touched_at']);

/**
 * A copy of `value` with every `undefined` object property removed, at any depth.
 *
 * Only plain objects and arrays are walked. Anything else — a Date, a Firestore
 * Timestamp or a FieldValue sentinel such as `serverTimestamp()` — is passed
 * through untouched, because rebuilding a class instance as a plain object
 * turns it into data Firestore no longer recognises.
 */
export function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => stripUndefinedDeep(item)) as T;
  if (!isPlainObject(value)) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    if (v === undefined) continue;
    out[k] = stripUndefinedDeep(v);
  }
  return out as T;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Turn a host's patch into the Firestore write that applies it.
 *
 * Replace when the stored state belongs to another scene (and this host has not
 * already replaced it for this one); merge otherwise. See the file comment for why.
 */
export function buildTeacherContentWrite(
  patch: Partial<TeacherContentState>,
  ctx: TeacherContentWriteContext
): TeacherContentWrite {
  const { sceneKey, syncId, previous, replacedScene } = ctx;

  // The fields this publish actually sets: undefined means "not in this patch",
  // and the context-owned meta keys are never taken from a patch.
  const fields: Array<[string, unknown]> = [];
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || CONTEXT_OWNED_KEYS.has(k)) continue;
    fields.push([k, stripUndefinedDeep(v)]);
  }

  const touched: Record<string, number> = {};
  for (const [k] of fields) {
    if (!META_KEYS.has(k)) touched[k] = syncId;
  }

  const sameScene = previous?.scene_key === sceneKey || replacedScene === sceneKey;

  if (!sameScene) {
    // A new scene inherits nothing: the object is rebuilt from this patch alone.
    const state: Record<string, unknown> = {
      scene_key: sceneKey,
      licensed_content_id: sceneKey,
      ...Object.fromEntries(fields),
      touched_at: touched,
      sync_id: syncId,
    };
    return { mode: 'replace', data: { [FIELD]: state } };
  }

  const data: Record<string, unknown> = {};
  for (const [k, v] of fields) data[`${FIELD}.${k}`] = v;
  for (const k of Object.keys(touched)) data[`${FIELD}.touched_at.${k}`] = syncId;
  data[`${FIELD}.sync_id`] = syncId;
  data[`${FIELD}.scene_key`] = sceneKey;
  return { mode: 'merge', data };
}
